import { useCountry } from '@lt/localization';
import classNames from 'classnames';
import React, { lazy, Suspense, useEffect } from 'react';
import { HotelCounter } from 'src/components/HotelCounter';
import type { SearchType } from 'src/types/search';
import { scrollToElement } from 'src/utils/domUtils';
import styled from 'styled-components';
import LazySubscribe from '../../../containers/Subscribe/LazySubscribe';
import LazyFilters from '../../Filters/components/FiltersPanel';
import Hotels from '../../Hotels';
import LazyPriceChart from '../../PriceChart/LazyPriceChart';
import Sorting from '../../Sorting';
import Title from '../../Title';

const EmptySearch = lazy(
  () => import(/* webpackChunkName: "EmptySearch" */ '../../EmptySearch'),
);
const EmptyFiltration = lazy(
  () =>
    import(/* webpackChunkName: "EmptyFiltration" */ '../../EmptyFiltration'),
);

const YandexMapsSearch = lazy(
  () =>
    import(/* webpackChunkName: "YandexMapsSearch" */ '../../YandexMapsSearch'),
);

type SearchPageMobileProps = {
  hasSearchResults?: boolean;
  mapView: boolean;
  hideFilters: boolean;
  isSubscribeShown: boolean;
  isEmptySearch: boolean;
  isEmptyFiltration: boolean;
  isHotelOnly: boolean;
  searchType: SearchType;
  hotelsCount: number;
  showResults: boolean;
};

const SearchPageMobile = ({
  hideFilters,
  mapView,
  hasSearchResults = false,
  isSubscribeShown,
  isEmptySearch,
  isEmptyFiltration,
  isHotelOnly,
  searchType,
  hotelsCount,
  showResults,
}: SearchPageMobileProps): JSX.Element => {
  useEffect(() => {
    scrollToElement(document.body);
  }, [hideFilters]);

  useEffect(() => {
    // Когда открываем/закрываем карту - скроллим вверх страницы
    window.scrollTo(0, 0);
  }, [mapView]);

  const showSorting = showResults && hasSearchResults;

  const showPriceChart =
    !mapView && hideFilters && !isSubscribeShown && !isHotelOnly;

  const isEmptyResult = isEmptySearch || isEmptyFiltration;
  const { isRussia } = useCountry();

  const contentClasses = classNames({
    'search-page__center': true,
    'search-page__center--mobile-map': mapView,
  });

  return (
    <div className="search-page-mobile">
      {showResults && <StyledTitle />}
      {showPriceChart && isRussia && !isEmptyResult && <PriceChart />}
      {!hideFilters && (
        <LazyFilters show={!hideFilters} isHotelOnly={isHotelOnly} isMobile />
      )}
      {isSubscribeShown && !isHotelOnly && isRussia ? <LazySubscribe /> : null}
      {showSorting ? (
        <SortingContainer>
          <Sorting />
          <HotelCounter hotelCounter={hotelsCount} searchType={searchType} />
        </SortingContainer>
      ) : null}

      <div className="search-page__content mobile">
        <div className={contentClasses}>
          {mapView ? (
            <Suspense fallback={null}>
              <YandexMapsSearch />
            </Suspense>
          ) : null}
          {showResults ? <Hotels /> : null}

          <Suspense fallback={null}>
            {isEmptySearch && <EmptySearch />}
            {isEmptyFiltration && <EmptyFiltration />}
          </Suspense>
        </div>
      </div>
    </div>
  );
};

const SortingContainer = styled.div`
  padding: 0 12px 8px 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledTitle = styled(Title)`
  margin: 0 10px 5px;
  padding-top: 10px;
`;

const PriceChart = styled(LazyPriceChart)`
  margin-bottom: 16px;
`;

export default SearchPageMobile;
